import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useTourStore from "../store/tourStore";

function TourList() {
  const navigate = useNavigate();
  const { tours, loading, error, fetchTours, deleteTour, filters, setFilters } = useTourStore();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterLang, setFilterLang] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [copiedShareId, setCopiedShareId] = useState(null);

  useEffect(() => {
    fetchTours();
  }, [fetchTours, filters]);

  const handleFilterChange = () => {
    setFilters({
      lang: filterLang || null,
      status: filterStatus || null,
    });
  };

  const handleEdit = (tourId) => {
    navigate(`/tours/${tourId}/edit`);
  };

  const handleDelete = async (tourId, tourTitle) => {
    if (window.confirm(`Are you sure you want to delete "${tourTitle}"?`)) {
      try {
        await deleteTour(tourId);
      } catch (err) {
        alert('Failed to delete tour: ' + err.message);
      }
    }
  };

  const copyShareLink = (shareId) => {
    const link = `${window.location.origin}/tour/${shareId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedShareId(shareId);
      setTimeout(() => setCopiedShareId(null), 2000);
    });
  };

  if (loading && tours.length === 0) {
    return <div className="loading">Loading tours...</div>;
  }

  return (
    <div className="tour-list">
      <div className="page-header">
        <h2>Tours</h2>
        <button 
          className="button button-primary" 
          onClick={() => setShowCreateModal(true)}
        >
          + Create New Tour
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="filters">
        <select 
          value={filterLang} 
          onChange={(e) => setFilterLang(e.target.value)}
          className="filter-select"
        >
          <option value="">All Languages</option>
          <option value="ru">Russian</option>
          <option value="en">English</option>
        </select>

        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>

        <button onClick={handleFilterChange} className="button button-secondary">
          Apply Filters
        </button>
      </div>

      {tours.length === 0 ? (
        <div className="card empty-state">
          <p>No tours found. Create your first tour to get started!</p>
        </div>
      ) : (
        <div className="tours-grid">
          {tours.map((tour) => (
            <div key={tour.id} className="tour-card">
              <div className="tour-card-header">
                <h3>{tour.title}</h3>
                <div className="tour-badges">
                  <span className="badge badge-lang">{tour.lang.toUpperCase()}</span>
                  <span className={`badge badge-${tour.status}`}>
                    {tour.status}
                  </span>
                </div>
              </div>
              
              <p className="tour-description">{tour.description || 'No description'}</p>
              
              <div className="tour-meta">
                <span>Base ID: {tour.base_id}</span>
                <span>Version: {tour.current_version}</span>
              </div>

              {tour.status === 'published' && tour.share_id && (
                <div className="tour-share">
                  <button 
                    onClick={() => copyShareLink(tour.share_id)}
                    className="button button-sm button-secondary"
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    {copiedShareId === tour.share_id ? '✓ Copied!' : '🔗 Copy Share Link'}
                  </button>
                </div>
              )}

              <div className="tour-actions">
                <button 
                  onClick={() => handleEdit(tour.id)}
                  className="button button-sm button-primary"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDelete(tour.id, tour.title)}
                  className="button button-sm button-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateTourModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
}

function CreateTourModal({ onClose }) {
  const navigate = useNavigate();
  const { createTour } = useTourStore();
  const [formData, setFormData] = useState({
    base_id: '',
    lang: 'ru',
    title: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const tour = await createTour(formData);
      navigate(`/tours/${tour.id}/edit`);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create New Tour</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-banner">{error}</div>}

          <div className="form-group">
            <label>Base ID *</label>
            <input
              type="text"
              value={formData.base_id}
              onChange={(e) => setFormData({ ...formData, base_id: e.target.value })}
              placeholder="e.g., london_red_route"
              required
            />
            <small>Unique identifier for grouping language versions</small>
          </div>

          <div className="form-group">
            <label>Language *</label>
            <select
              value={formData.lang}
              onChange={(e) => setFormData({ ...formData, lang: e.target.value })}
              required
            >
              <option value="ru">Russian (RU)</option>
              <option value="en">English (EN)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., London Red Route Audio Tour"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the tour"
              rows="3"
            />
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              onClick={onClose} 
              className="button button-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="button button-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Tour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TourList;
